import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { controllingAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Search, Filter, Database, 
  DollarSign, Users, Package, Plane, Settings, Building2,
  TrendingUp, ArrowLeftRight, ChevronLeft, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Cost Element Categories
const CATEGORIES = [
  { value: '1', label: 'Primary Costs (Expenses)', description: 'Direct costs linked to GL accounts' },
  { value: '11', label: 'Revenue', description: 'Revenue cost elements' },
  { value: '12', label: 'Sales Deduction', description: 'Sales deductions' },
  { value: '21', label: 'Internal Settlement', description: 'Settlement to internal receivers' },
  { value: '22', label: 'External Settlement', description: 'Settlement to external receivers' },
  { value: '31', label: 'Results Analysis', description: 'Order/project results analysis' },
  { value: '41', label: 'Overhead Rates', description: 'Overhead surcharges' },
  { value: '42', label: 'Assessment', description: 'Cost center/profit center assessment' },
  { value: '43', label: 'Internal Activity', description: 'Internal activity allocation' },
  { value: '90', label: 'Accrual', description: 'Accrual calculation' },
];

// Cost Element Types
const TYPES = [
  { value: 'PERSONNEL', label: 'Personnel', icon: Users },
  { value: 'MATERIAL', label: 'Material', icon: Package },
  { value: 'SERVICES', label: 'Services', icon: Settings },
  { value: 'TRAVEL', label: 'Travel', icon: Plane },
  { value: 'EQUIPMENT', label: 'Equipment', icon: Database },
  { value: 'OVERHEAD', label: 'Overhead', icon: Building2 },
  { value: 'DEPRECIATION', label: 'Depreciation', icon: TrendingUp },
  { value: 'REVENUE', label: 'Revenue', icon: DollarSign },
  { value: 'INTERNAL', label: 'Internal', icon: ArrowLeftRight },
  { value: 'OTHER', label: 'Other', icon: Settings },
];

const CostElements = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [costElements, setCostElements] = useState([]);
  const [controllingAreas, setControllingAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    controlling_area_id: '',
    category: '',
    cost_element_type: '',
    status: ''
  });
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [formData, setFormData] = useState({
    cost_element_id: '',
    name: '',
    category: '1',
    cost_element_type: 'OTHER',
    controlling_area_id: '',
    gl_account_id: '',
    valid_from: '',
    valid_to: '',
    unit_of_measure: '',
    functional_area: '',
    long_text: '',
    status: 'Active'
  });


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [elementsRes, caRes] = await Promise.all([
        controllingAPI.getCostElements(filters),
        controllingAPI.getControllingAreas()
      ]);
      setCostElements(elementsRes.data);
      setControllingAreas(caRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load cost elements');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await controllingAPI.getCostElements({
        ...filters,
        search: searchTerm
      });
      setCostElements(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const caId = filters.controlling_area_id || '1000';
      const response = await controllingAPI.seedDefaultCostElements(caId);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to seed default cost elements');
    }
  };

  const resetForm = () => {
    setFormData({
      cost_element_id: '',
      name: '',
      category: '1',
      cost_element_type: 'OTHER',
      controlling_area_id: filters.controlling_area_id || '',
      gl_account_id: '',
      valid_from: '',
      valid_to: '',
      unit_of_measure: '',
      functional_area: '',
      long_text: '',
      status: 'Active'
    });
    setEditMode(false);
    setSelectedElement(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (element) => {
    setFormData({
      ...element,
      valid_from: element.valid_from || '',
      valid_to: element.valid_to || ''
    });
    setSelectedElement(element);
    setEditMode(true);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.cost_element_id || !formData.name) {
      toast.error('Cost Element ID and Name are required');
      return;
    }

    try {
      if (editMode) {
        await controllingAPI.updateCostElement(formData.cost_element_id, formData);
        toast.success('Cost element updated successfully');
      } else {
        await controllingAPI.createCostElement(formData);
        toast.success('Cost element created successfully');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const getCategoryLabel = (value) => {
    const cat = CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const getTypeIcon = (type) => {
    const t = TYPES.find(t => t.value === type);
    if (t) {
      const Icon = t.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <Settings className="h-4 w-4" />;
  };

  // Stats
  const stats = {
    total: costElements.length,
    primary: costElements.filter(e => e.category === '1').length,
    secondary: costElements.filter(e => ['41', '42', '43'].includes(e.category)).length,
    active: costElements.filter(e => e.status === 'Active').length
  };

  const filteredElements = costElements.filter(e => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return e.cost_element_id.toLowerCase().includes(search) ||
             e.name.toLowerCase().includes(search);
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/controlling')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Cost Elements</h1>
              <p className="text-sm text-slate-500">Manage cost element master data for controlling</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSeedDefaults}>
              <Download className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              New Cost Element
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <p className="text-sm text-orange-600">Total Elements</p>
              <p className="text-2xl font-bold text-orange-700">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <p className="text-sm text-green-600">Primary Costs</p>
              <p className="text-2xl font-bold text-green-700">{stats.primary}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <p className="text-sm text-orange-600">Secondary Costs</p>
              <p className="text-2xl font-bold text-orange-700">{stats.secondary}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <p className="text-sm text-emerald-600">Active</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.active}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by ID or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-48">
                <Label className="text-xs">Category</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters(p => ({ ...p, category: v === '__all__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Categories</SelectItem>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs">Type</Label>
                <Select value={filters.cost_element_type} onValueChange={(v) => setFilters(p => ({ ...p, cost_element_type: v === '__all__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Types</SelectItem>
                    {TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label className="text-xs">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters(p => ({ ...p, status: v === '__all__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSearch}>
                <Filter className="h-4 w-4 mr-2" />
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-32">Cost Element</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-40">Category</TableHead>
                  <TableHead className="w-32">Type</TableHead>
                  <TableHead className="w-32">GL Account</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">Loading...</TableCell>
                  </TableRow>
                ) : filteredElements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No cost elements found. Click "Seed Defaults" to create standard cost elements.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredElements.map(element => (
                    <TableRow key={element.cost_element_id} className="hover:bg-slate-50">
                      <TableCell className="font-mono font-medium text-orange-600">{element.cost_element_id}</TableCell>
                      <TableCell className="font-medium">{element.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getCategoryLabel(element.category)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTypeIcon(element.cost_element_type)}
                          <span className="text-sm">{element.cost_element_type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{element.gl_account_id || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={element.status === 'Active' ? 'default' : 'secondary'}>
                          {element.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(element)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {filteredElements.length > 0 && (
              <div className="px-4 py-3 border-t text-sm text-slate-500">
                Showing {filteredElements.length} of {costElements.length} cost elements
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Cost Element' : 'Create Cost Element'}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Cost Element ID *</Label>
                <Input
                  value={formData.cost_element_id}
                  onChange={(e) => setFormData(p => ({ ...p, cost_element_id: e.target.value }))}
                  placeholder="e.g., 410000"
                  disabled={editMode}
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Cost element name"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.value} - {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.cost_element_type} onValueChange={(v) => setFormData(p => ({ ...p, cost_element_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Controlling Area</Label>
                <Select value={formData.controlling_area_id || '__none__'} onValueChange={(v) => setFormData(p => ({ ...p, controlling_area_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {controllingAreas.map(ca => (
                      <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                        {ca.controlling_area_id} - {ca.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>GL Account (Primary Cost Elements)</Label>
                <Input
                  value={formData.gl_account_id || ''}
                  onChange={(e) => setFormData(p => ({ ...p, gl_account_id: e.target.value }))}
                  placeholder="e.g., 410000"
                />
              </div>
              <div className="space-y-2">
                <Label>Valid From</Label>
                <Input
                  type="date"
                  value={formData.valid_from || ''}
                  onChange={(e) => setFormData(p => ({ ...p, valid_from: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Valid To</Label>
                <Input
                  type="date"
                  value={formData.valid_to || ''}
                  onChange={(e) => setFormData(p => ({ ...p, valid_to: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit of Measure</Label>
                <Input
                  value={formData.unit_of_measure || ''}
                  onChange={(e) => setFormData(p => ({ ...p, unit_of_measure: e.target.value }))}
                  placeholder="e.g., HR, PC"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.long_text || ''}
                  onChange={(e) => setFormData(p => ({ ...p, long_text: e.target.value }))}
                  placeholder="Additional description..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit}>{editMode ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CostElements;
